import { graphql, useStaticQuery } from "gatsby"
import type { DatoLocale } from "../types"

const namespaces = [
  "contactCategorySelect",
  "shipmentTracking",
  "productSupportCategorySelect",
  "productAgeSelect",
  "formCommon",
  "interestSelect",
  "formErrors",
  "formContact",
  "saunaCalculator",
  "product",
  "contact",
  "article",
  "productFinder",
  "common",
  "locale",
] as const

type Namespace = (typeof namespaces)[number]

type MicrocopyObject = Record<string, string>

type StoreNamespace = {
  [locale in DatoLocale]?: MicrocopyObject
}

type Store = { [ns in Namespace]?: StoreNamespace }

let _store: Store | null = null

type QueryData = {
  allDatoCmsMicrocopy: {
    nodes: {
      namespace: Namespace
      _allCopyLocales: {
        locale: string
        value: string
      }[]
    }[]
  }
}

/**
 * Parse Microcopy data from DatoCMS
 */
function parseMicrocopy(jsonString: string): MicrocopyObject {
  return JSON.parse(jsonString)
}
/**
 * Get a translation from Dato _Translations_ content.
 *
 * Returns: Translated value matching the given `key` or `defaultValue` if not found
 */
export type GetMicrocopy = (
  /** Name of the field in _Translations_ type of entry */
  key: string,
  /** Value returned if no translation found by _key_ */
  defaultValue?: string,
  /** vewrite _namespace_ defined at useTranslation() call for this translation */
  namespaceOverwrite?: Namespace
) => string
/**
 * Temporary drop-in replacement for useDatoTranslation() while we're moving to using
 * a library (like i18next) to handle translations.
 * useDatoTranslation() used Dato transalation_* single instance models as data source.
 * This uses new microcopy records
 */
const useDatoMicrocopy = (namespace: Namespace, locale: DatoLocale): GetMicrocopy => {
  const data = useStaticQuery<QueryData>(graphql`
    query AllDatoMicrocopy {
      allDatoCmsMicrocopy {
        nodes {
          namespace
          _allCopyLocales {
            value
            locale
          }
        }
      }
    }
  `)

  const t: GetMicrocopy = (key: string, defaultValue?: string, namespaceOverwrite?: Namespace) => {
    const effectiveNamespace = namespaceOverwrite || namespace
    if (!_store || !effectiveNamespace) {
      console.warn(`useDatoMicrocopy() no translations or missing namespace`)
      return defaultValue || ""
    }
    const data = _store[effectiveNamespace]
    if (!data) {
      console.warn(`useDatoMicrocopy() no translations in namespace ${effectiveNamespace}`)
      return defaultValue || ""
    }

    const effectiveLocaleData = data[locale] !== undefined ? data[locale] : data["en"]

    if (
      effectiveLocaleData === undefined ||
      effectiveLocaleData === null ||
      (!effectiveLocaleData[key] && defaultValue === undefined)
    ) {
      console.warn(
        `useDatoMicrocopy() no value and default value missing for key "${key}" in namespace ${effectiveNamespace} (locale "${locale}")`
      )
    }

    return effectiveLocaleData !== undefined && effectiveLocaleData !== null
      ? effectiveLocaleData[key] || defaultValue || ""
      : ""
  }

  if (!data) {
    console.error("Microcopy data fetch failed")
    return t
  }

  if (_store !== null) {
    // we've already parsed and re-arranged raw data from Dato and are ready
    return t
  }

  // parse and re-arrange raw data from Dato to more useful structure
  _store = {}
  const nodes = data.allDatoCmsMicrocopy.nodes
  for (const { _allCopyLocales, namespace } of nodes) {
    for (const copy of _allCopyLocales) {
      const parsed = parseMicrocopy(copy.value)
      const ns = _store[namespace]
      if (!ns) {
        _store[namespace] = { [copy.locale]: parsed }
      } else {
        ns[copy.locale] = parsed
      }
    }
  }

  return t
}

export default useDatoMicrocopy
